/**
 * LiveCoachPanel — In-call AI sales coach (BW-05 P4).
 *
 * Continuously records 4-second audio chunks during a live call, sends each
 * chunk to /api/dialer/transcribe (Whisper via Emergent LLM key), appends the
 * text to a rolling transcript, and after every new chunk asks GPT-5 Mini for
 * fresh coaching guidance (sentiment, suggestion, objection response, next
 * discovery question, and structured lead-field auto-fills).
 *
 * Audio sources:
 *   • rep   — local microphone via getUserMedia
 *   • prospect — RC WebPhone session.mediaStream (optional; passed in via prop)
 *
 * If `webPhoneSession` is not provided we still capture the rep side, which is
 * enough to surface the most common objection responses and discovery prompts.
 */
import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Mic, MicOff, Sparkles, MessageCircle, HelpCircle, CheckCircle2, Activity, Loader2, Power, PowerOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const CHUNK_MS = 4000;
const COACH_INTERVAL_CHUNKS = 1; // run coach after every new transcript chunk

const sentimentColor = (s) => ({
  positive: "#10b981",
  neutral: "#94a3b8",
  negative: "#ef4444",
}[s] || "#94a3b8");

export default function LiveCoachPanel({
  active,                    // true when a call is live
  lead,                      // lead object for context
  campaignFocus,             // optional string
  webPhoneSession,           // optional RC WebPhone CallSession for prospect audio
  onSentimentChange,         // (s) => void — parent updates the sentiment pill
  onFieldUpdates,            // (updates[]) => void — auto-apply to dispForm
  onTranscriptUpdate,        // (transcript[]) => void — parent can use this for follow-up email
  externalId,                // optional RC call/session ID — used to persist transcript
}) {
  const [recording, setRecording] = useState(false);
  const [transcript, setTranscript] = useState([]); // [{speaker, text, at}]
  const [coach, setCoach] = useState({ sentiment: "neutral", suggestion: "", objection_response: "", next_question: "", field_updates: [] });
  const [coachLoading, setCoachLoading] = useState(false);
  const [error, setError] = useState(null);
  const [chunkCount, setChunkCount] = useState(0);
  const [coachEnabled, setCoachEnabled] = useState(true);

  const recRef = useRef(null);
  const streamRef = useRef(null);
  const transcriptRef = useRef([]);
  const coachInflightRef = useRef(false);
  const scrollRef = useRef(null);
  const callStartRef = useRef(null);

  // Load coach on/off preference once per mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/users/me/coach-settings`, { headers: authHeaders() });
        setCoachEnabled(!!data?.coach_enabled);
      } catch (e) { console.warn("coach-settings GET failed", e); }
    })();
  }, []);

  const toggleCoach = useCallback(async () => {
    const next = !coachEnabled;
    setCoachEnabled(next);
    try {
      await axios.put(`${API}/users/me/coach-settings`, { coach_enabled: next }, { headers: authHeaders() });
    } catch (e) {
      console.warn("coach-settings PUT failed", e);
    }
  }, [coachEnabled]);

  // Stop recording cleanly
  const stop = useCallback(() => {
    try { recRef.current?.stop(); } catch {}
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
    recRef.current = null;
    streamRef.current = null;
    setRecording(false);
  }, []);

  // Send a single audio chunk to /transcribe
  const sendChunk = useCallback(async (blob, speaker) => {
    if (!blob || blob.size < 1000) return; // skip silent < 1KB chunks
    const fd = new FormData();
    fd.append("file", blob, `chunk-${Date.now()}.webm`);
    fd.append("speaker", speaker);
    fd.append("language", "en");
    try {
      const { data } = await axios.post(`${API}/dialer/transcribe`, fd, {
        headers: { ...authHeaders() },
      });
      const text = (data?.text || "").trim();
      if (!text) return;
      const entry = { speaker, text, at: new Date().toISOString() };
      transcriptRef.current = [...transcriptRef.current, entry].slice(-100);
      setTranscript(transcriptRef.current);
      if (onTranscriptUpdate) onTranscriptUpdate(transcriptRef.current);
      setChunkCount((c) => c + 1);
    } catch (e) {
      console.warn("transcribe failed", e);
      // Soft-fail; keep recording so a single network blip doesn't kill coaching
    }
  }, []);

  // Fetch fresh coach guidance from /live-coach
  const refreshCoach = useCallback(async () => {
    if (coachInflightRef.current) return;
    if (transcriptRef.current.length < 1) return;
    coachInflightRef.current = true;
    setCoachLoading(true);
    try {
      const { data } = await axios.post(`${API}/dialer/live-coach`, {
        transcript: transcriptRef.current.slice(-20),
        lead: lead || null,
        campaign_focus: campaignFocus || null,
      }, { headers: authHeaders() });
      setCoach(data);
      if (data.sentiment && onSentimentChange) onSentimentChange(data.sentiment);
      if (data.field_updates?.length && onFieldUpdates) onFieldUpdates(data.field_updates);
    } catch (e) {
      console.warn("live-coach failed", e);
    } finally {
      coachInflightRef.current = false;
      setCoachLoading(false);
    }
  }, [lead, campaignFocus, onSentimentChange, onFieldUpdates]);

  // Whenever a new transcript chunk lands, ask the coach (debounced via inflight ref).
  useEffect(() => {
    if (chunkCount > 0 && chunkCount % COACH_INTERVAL_CHUNKS === 0) {
      refreshCoach();
    }
  }, [chunkCount, refreshCoach]);

  // Auto-scroll transcript
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [transcript]);

  // ── Start / stop the recorder based on `active` ──
  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setError("Browser doesn't support microphone capture");
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;

        // Prefer Opus/WebM — Whisper accepts it natively
        const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
        const rec = new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 32000 });
        recRef.current = rec;

        rec.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) sendChunk(e.data, "rep");
        };
        rec.onerror = (e) => console.warn("recorder error", e);

        rec.start(CHUNK_MS);
        setRecording(true);
        setError(null);
      } catch (e) {
        console.error("LiveCoach start failed", e);
        setError(/permission|notallowed/i.test(String(e?.message || e))
          ? "Microphone blocked — grant access in your browser to enable live coach"
          : `Mic capture failed: ${e.message || e}`);
      }
    };

    if (active && coachEnabled) {
      callStartRef.current = Date.now();
      start();
    } else {
      stop();
    }
    return () => { cancelled = true; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, coachEnabled]);

  // Persist transcript when the call ends (active → false). The coach may
  // have processed many chunks but the call_transcripts row is only written
  // once, here, so admins can later replay the conversation.
  const lastActiveForFlushRef = useRef(active);
  useEffect(() => {
    if (lastActiveForFlushRef.current && !active && transcriptRef.current.length > 0) {
      const durationSec = callStartRef.current
        ? Math.max(1, Math.floor((Date.now() - callStartRef.current) / 1000))
        : 0;
      axios.post(`${API}/dialer/call-transcript`, {
        external_id: externalId,
        lead_id: lead?.id,
        transcript: transcriptRef.current,
        duration_sec: durationSec,
        started_at: callStartRef.current ? new Date(callStartRef.current).toISOString() : null,
        ended_at: new Date().toISOString(),
        sentiment_final: coach.sentiment,
        coach_summary: [coach.suggestion, coach.objection_response, coach.next_question]
          .filter(Boolean).join(" · ").slice(0, 600) || null,
      }, { headers: authHeaders() }).catch((e) => console.warn("transcript persist failed", e));
    }
    lastActiveForFlushRef.current = active;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Reset transcript when a new call starts (active goes false→true).
  const lastActiveRef = useRef(active);
  useEffect(() => {
    if (active && !lastActiveRef.current) {
      transcriptRef.current = [];
      setTranscript([]);
      setCoach({ sentiment: "neutral", suggestion: "", objection_response: "", next_question: "", field_updates: [] });
      setChunkCount(0);
    }
    lastActiveRef.current = active;
  }, [active]);

  if (!active) return null;

  return (
    <div data-testid="live-coach-panel" style={{
      background: "#fff", border: "0.5px solid var(--border, #e2e8f0)", borderRadius: 12,
      padding: 14, display: "flex", flexDirection: "column", gap: 12,
      fontFamily: "inherit",
    }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "3px 8px", borderRadius: 999,
            background: recording ? "#fee2e2" : "#f1f5f9",
            color: recording ? "#dc2626" : "#64748b",
            fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em",
          }} data-testid="coach-rec-state">
            {recording
              ? <><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#dc2626", animation: "rec-pulse 1.2s ease-in-out infinite" }} /> recording</>
              : <><MicOff style={{ width: 10, height: 10 }} /> idle</>}
          </div>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--navy, #0f172a)" }}>AI Live Coach</span>
          {coachLoading && <Loader2 style={{ width: 11, height: 11, color: "var(--blue, #5B6CFF)", animation: "spin 1s linear infinite" }} />}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            data-testid="coach-toggle-btn"
            onClick={toggleCoach}
            title={coachEnabled ? "Disable AI coach for your calls" : "Enable AI coach"}
            style={{
              padding: "3px 8px", fontSize: 10, fontWeight: 600,
              background: coachEnabled ? "#dcfce7" : "#fee2e2",
              color: coachEnabled ? "#166534" : "#991b1b",
              border: `0.5px solid ${coachEnabled ? "#86efac" : "#fecaca"}`,
              borderRadius: 999, cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 4,
              textTransform: "uppercase", letterSpacing: "0.06em",
              fontFamily: "inherit",
            }}
          >
            {coachEnabled ? <Power style={{ width: 10, height: 10 }} /> : <PowerOff style={{ width: 10, height: 10 }} />}
            {coachEnabled ? "On" : "Off"}
          </button>
          <div style={{
            display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11,
          }} data-testid="coach-sentiment-chip">
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: sentimentColor(coach.sentiment) }} />
            <span style={{ color: "var(--text-3, #64748b)", textTransform: "capitalize" }}>{coach.sentiment}</span>
          </div>
        </div>
      </div>

      {error && (
        <div data-testid="coach-error" style={{
          fontSize: 11, padding: 8, borderRadius: 8,
          background: "#fef3c7", border: "0.5px solid #fcd34d", color: "#92400e",
        }}>
          {error}
        </div>
      )}

      {/* Coach cards */}
      {(coach.suggestion || coach.objection_response || coach.next_question) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {coach.suggestion && (
            <div data-testid="coach-suggestion" style={{
              background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
              border: "0.5px solid #6ee7b7", borderRadius: 8, padding: 10,
              display: "flex", alignItems: "flex-start", gap: 8,
            }}>
              <Sparkles style={{ width: 13, height: 13, color: "#059669", flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, color: "#047857", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>Live tip</div>
                <div style={{ fontSize: 12, color: "#064e3b", lineHeight: 1.45 }}>{coach.suggestion}</div>
              </div>
            </div>
          )}
          {coach.objection_response && (
            <div data-testid="coach-objection" style={{
              background: "#fff7ed", border: "0.5px solid #fdba74", borderRadius: 8, padding: 10,
              display: "flex", alignItems: "flex-start", gap: 8,
            }}>
              <MessageCircle style={{ width: 13, height: 13, color: "#c2410c", flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, color: "#9a3412", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>Objection response</div>
                <div style={{ fontSize: 12, color: "#7c2d12", lineHeight: 1.45 }}>{coach.objection_response}</div>
              </div>
            </div>
          )}
          {coach.next_question && (
            <div data-testid="coach-next-question" style={{
              background: "#eff6ff", border: "0.5px solid #93c5fd", borderRadius: 8, padding: 10,
              display: "flex", alignItems: "flex-start", gap: 8,
            }}>
              <HelpCircle style={{ width: 13, height: 13, color: "#2563eb", flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontSize: 9, fontWeight: 700, color: "#1d4ed8", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 2 }}>Next question</div>
                <div style={{ fontSize: 12, color: "#1e3a8a", lineHeight: 1.45 }}>{coach.next_question}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Auto-filled lead fields */}
      {coach.field_updates?.length > 0 && (
        <div data-testid="coach-field-updates" style={{
          background: "#faf5ff", border: "0.5px solid #d8b4fe", borderRadius: 8, padding: 10,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
            <CheckCircle2 style={{ width: 12, height: 12, color: "#9333ea" }} />
            <span style={{ fontSize: 9, fontWeight: 700, color: "#6b21a8", textTransform: "uppercase", letterSpacing: "0.06em" }}>Auto-detected from call</span>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
            {coach.field_updates.map((u, i) => (
              <span key={`${u.field}-${i}`} data-testid={`coach-field-${u.field}`} style={{
                fontSize: 10, padding: "2px 8px", borderRadius: 6,
                background: "#fff", border: "0.5px solid #d8b4fe", color: "#581c87",
              }}>
                <strong>{u.field}:</strong> {u.value}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Live transcript */}
      <div>
        <div style={{ fontSize: 9, fontWeight: 700, color: "var(--text-4, #94a3b8)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
          <Activity style={{ width: 10, height: 10 }} /> Transcript ({transcript.length} chunks)
        </div>
        <div
          ref={scrollRef}
          data-testid="coach-transcript"
          style={{
            maxHeight: 160, overflowY: "auto",
            background: "var(--surface-2, #f8fafc)",
            border: "0.5px solid var(--border, #e2e8f0)",
            borderRadius: 8, padding: 8, fontSize: 11, lineHeight: 1.5,
            color: "var(--navy, #0f172a)",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
          }}
        >
          {transcript.length === 0 ? (
            <div style={{ color: "var(--text-4)", fontStyle: "italic" }}>
              Listening… first transcript chunk arrives in ~5 seconds.
            </div>
          ) : (
            transcript.map((t, i) => (
              <div key={i} style={{ marginBottom: 4 }}>
                <span style={{
                  color: t.speaker === "prospect" ? "#9333ea" : "#0369a1",
                  fontWeight: 700,
                  marginRight: 4,
                }}>
                  {t.speaker === "prospect" ? "PROSPECT" : "REP"}:
                </span>
                <span>{t.text}</span>
              </div>
            ))
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
        @keyframes rec-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
}
